import { useMutation } from '@apollo/react-hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Prompt, useLocation } from 'react-router';
import { useLocation as useLocationDom } from 'react-router-dom';

import { useAuth } from '../../../../context';
import {
  ClaimVerificationRequest,
  ClaimVerificationRequestVariables,
} from '../../../../generated/ClaimVerificationRequest';
import {
  VerificationRequests_requestVerificationAdvance_row,
  VerificationRequestsVariables,
} from '../../../../generated/VerificationRequests';
import {
  RoleAdmin,
  TypeVerify,
  VerificationClaimType,
} from '../../../../generated/globalTypes';
import { routePaths } from '../../constants/routes';
import { spacing } from '../../constants/theme';
import { useSnackbar } from '../../context';
import { CLAIM_VERIFICATION_REQUEST } from '../../graphql/mutations';
import { VerificationRequestsQueryResult } from '../TokoVerification';
// import { GET_AGENT_BY_STORE_ID } from '../../../../graphql/queries';
// import {
//   AgentByStoreId,
//   AgentByStoreIdVariables,
// } from '../../../../generated/AgentByStoreId';

import LeftPane from './LeftPane';
import OwnerDataForm from './OwnerDataForm';
import TokoDataForm from './TokoDataForm';

type LocationState = {
  data: VerificationRequests_requestVerificationAdvance_row;
};

type Location = {
  pathname: string;
  state?: LocationState;
};

const WorkInProgressScene = ({
  onFilterChange,
  ...verificationRequestsQueryResult
}: {
  onFilterChange: (filter: VerificationRequestsVariables) => void;
} & VerificationRequestsQueryResult) => {
  const { state }: Location = useLocationDom();

  let { refetch } = verificationRequestsQueryResult;

  const { openSnackbar } = useSnackbar();
  const { role } = useAuth();
  const [
    selectedItem,
    setSelectedItem,
  ] = useState<VerificationRequests_requestVerificationAdvance_row | null>(
    state?.data || null,
  );

  const [formDirty, setFormDirty] = useState(false);
  const location = useLocation();

  const onUnclaim = useCallback(() => {
    refetch();
    setSelectedItem(null);
    openSnackbar({
      variant: 'success',
      message: t([
        'Klaim permohonan verifikasi telah dibatalkan.',
        'Verification request has been unclaimed.',
      ]),
    });
  }, [openSnackbar, refetch]);
  const onUpdate = useCallback(() => {
    refetch();
    setSelectedItem(null);
    openSnackbar({
      variant: 'success',
      message: t(['Data telah diperbarui.', 'Data has been updated.']),
    });
  }, [openSnackbar, refetch]);

  const onUnclaimError = useCallback(() => {
    openSnackbar({
      variant: 'error',
      message: t([
        'Gagal membatalkan klaim permohonan verifikasi. Mohon coba lagi nanti.',
        'Failed to unclaim verification request. Please try again later.',
      ]),
    });
  }, [openSnackbar]);

  const [unclaim, { loading: unclaimLoading }] = useMutation<
    ClaimVerificationRequest,
    ClaimVerificationRequestVariables
  >(CLAIM_VERIFICATION_REQUEST, {
    onCompleted: onUnclaim,
    onError: onUnclaimError,
  });

  const unclaimItem = useCallback(() => {
    if (selectedItem) {
      unclaim({
        variables: { type: VerificationClaimType.UNCLAIM, id: selectedItem.id },
      });
    }
  }, [selectedItem, unclaim]);

  // const [getAgent, { data: getAgenData }] = useLazyQuery<
  //   AgentByStoreId,
  //   AgentByStoreIdVariables
  // >(GET_AGENT_BY_STORE_ID);

  const onSelectLeftPaneItem = useCallback(
    (value: VerificationRequests_requestVerificationAdvance_row | null) => {
      if (selectedItem?.id !== value?.id) {
        // TODO: may need to block this if form is dirty
        setSelectedItem(value);
        setFormDirty(false);
        // getAgent({
        //   variables: {
        //     managerStoreId: value.storeId || '',
        //   },
        // });
      }
    },
    [selectedItem],
  );

  const shouldBlock = useMemo(() => {
    return location.pathname === routePaths.wip && formDirty;
  }, [formDirty, location]);

  useEffect(() => {
    if (shouldBlock) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
    return () => {
      // NOTE: cleanup
      window.onbeforeunload = null;
    };
  }, [shouldBlock]);

  const formContent = useMemo(() => {
    if (!selectedItem) {
      return null;
    } else if (selectedItem.type === TypeVerify.TOKO) {
      return (
        <TokoDataForm
          unclaimLoading={unclaimLoading}
          onUnclaim={unclaimItem}
          data={selectedItem}
          // distributors={(getAgenData && getAgenData.agentByStoreId) || []}
          onUpdate={onUpdate}
          setFormDirty={setFormDirty}
          key={selectedItem.id}
        />
      );
    } else {
      return (
        <OwnerDataForm
          data={selectedItem}
          unclaimLoading={unclaimLoading}
          setFormDirty={setFormDirty}
          onUnclaim={unclaimItem}
          onUpdate={onUpdate}
          isMasterAdminRole={role === RoleAdmin.MASTER_VERIFICATION_ADMIN}
          isWip={true}
          key={selectedItem.id}
        />
      );
    }
  }, [selectedItem, unclaimLoading, role, unclaimItem, onUpdate]);

  return (
    <View style={styles.root}>
      <Prompt
        when={shouldBlock}
        message={t([
          'Apakah anda yakin ingin meninggalkan halaman ini?',
          'Are you sure you want to leave this page?',
        ])}
      />
      <View style={styles.left}>
        <LeftPane
          {...verificationRequestsQueryResult}
          shouldRefetch={false}
          selectedItemId={selectedItem?.id ?? null}
          setSelectedItem={onSelectLeftPaneItem}
          onFilterChange={onFilterChange}
        />
      </View>
      <View style={styles.right}>
        <ScrollView contentContainerStyle={styles.content}>
          {formContent}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    height: '100%',
  },
  left: {
    flex: 1,
  },
  right: {
    flex: 3.5,
  },
  content: {
    alignItems: 'center',
    paddingVertical: spacing.l,
  },
});

export default WorkInProgressScene;
